import _ from 'lodash';
import { generateFieldId } from '../../../../components/InputField/helper';
import {
    buildFieldValueMap,
    createAutoSaveExcludePath,
    createDeleteOperation,
    createFieldUpdateOperation,
    createFullUpdateOperation,
    createPlan,
    createPostOperation,
    normalizeRecordPayload,
    toLodashPath,
} from './shared';

const STRUCTURAL_MODE = {
    // Array items are standalone backend records, for example Business Plus UBO rows.
    STANDALONE_RECORD_ARRAY: 'standalone-record-array',
    // Array items are embedded in the primary document, so any structural change must
    // replace the whole array instead of patching indexed leaves.
    WHOLE_ARRAY_UPDATE_FIELD: 'whole-array-update-field',
};

const normalizeIndexedFieldPath = (path = '') => path
    .replace(/\[(\d+)\]/g, '[]')
    .replace(/\.([0-9]+)(?=\.|$)/g, '[]');

const extractArrayIndexFromPath = (path = '') => {
    const match = String(path || '').match(/\[(\d+)\]|\.([0-9]+)(?=\.|$)/);
    if (!match) {
        return null;
    }

    const resolvedIndex = Number(match[1] ?? match[2]);
    return Number.isInteger(resolvedIndex) ? resolvedIndex : null;
};

const normalizeArrayFieldPath = (field = '') => field.replace(/\[\]$/g, '');

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getFieldCandidates = (path = '') => {
    if (!path) return [];
    const normalizedPath = normalizeIndexedFieldPath(path);
    return [...new Set([path, normalizedPath].filter(Boolean))];
};

const candidateMatchesPath = (metadataPath, pathCandidates = []) => {
    if (!metadataPath || !pathCandidates.length) {
        return false;
    }

    return getFieldCandidates(metadataPath)
        .some((candidate) => pathCandidates.includes(candidate));
};

const getInheritedFieldCandidates = (path = '') => {
    const normalizedPath = normalizeIndexedFieldPath(path);
    if (!normalizedPath) {
        return [];
    }

    const segments = normalizedPath.split('.');
    const candidates = [];

    for (let index = segments.length; index > 0; index -= 1) {
        candidates.push(segments.slice(0, index).join('.'));
    }

    return [...new Set(candidates.filter(Boolean))];
};

const resolveFieldDefinition = (fieldDefinitions, path) => {
    if (!fieldDefinitions || typeof fieldDefinitions !== 'object' || !path) {
        return null;
    }

    for (const candidate of getFieldCandidates(path)) {
        if (fieldDefinitions[candidate]) {
            return fieldDefinitions[candidate];
        }
    }

    const pathCandidates = getFieldCandidates(path);
    for (const fieldDefinition of Object.values(fieldDefinitions || {})) {
        if (!fieldDefinition || typeof fieldDefinition !== 'object') {
            continue;
        }

        if (candidateMatchesPath(fieldDefinition?.countryCodeField, pathCandidates)) {
            return {
                ...fieldDefinition,
                field: path,
                type: 'integer',
                uiType: 'numberField',
            };
        }

        if (candidateMatchesPath(fieldDefinition?.phoneVerifiedViaOTPField, pathCandidates)) {
            return {
                ...fieldDefinition,
                field: path,
                type: 'boolean',
                uiType: 'radioBoolean',
            };
        }

        if (candidateMatchesPath(fieldDefinition?.phoneField, pathCandidates)) {
            return {
                ...fieldDefinition,
                field: path,
                type: 'string',
            };
        }
    }

    return null;
};

const resolveArrayItemFieldDefinition = (fieldDefinitions, arrayField, relativePath, arrayIndex) => {
    if (!relativePath) {
        return null;
    }

    return resolveFieldDefinition(fieldDefinitions, `${arrayField}[${arrayIndex}].${relativePath}`)
        || resolveFieldDefinition(fieldDefinitions, `${arrayField}.${relativePath}`);
};

const appendMetaMapValue = (existingMeta = null, mapKey, entryKey, value) => {
    if (!mapKey || !entryKey || !value) {
        return existingMeta || null;
    }

    const nextMeta = {
        ...(existingMeta || {}),
    };
    const nextMap = {
        ...(nextMeta?.[mapKey] || {}),
    };
    const existingValues = Array.isArray(nextMap?.[entryKey])
        ? nextMap[entryKey]
        : (nextMap?.[entryKey] ? [nextMap[entryKey]] : []);

    nextMap[entryKey] = _.uniq([...existingValues, value].filter(Boolean));
    nextMeta[mapKey] = nextMap;
    return nextMeta;
};

const mergeOperationMeta = (existingMeta = null, nextMeta = null) => {
    if (!existingMeta) {
        return nextMeta ? _.cloneDeep(nextMeta) : null;
    }

    if (!nextMeta) {
        return existingMeta;
    }

    const mergedMeta = {
        ...existingMeta,
        ...nextMeta,
    };

    ['sourceFieldPathMap', 'sourceFieldIdMap'].forEach((mapKey) => {
        const currentMap = existingMeta?.[mapKey];
        const incomingMap = nextMeta?.[mapKey];

        if (!currentMap && !incomingMap) {
            return;
        }

        const mergedMap = {
            ...(currentMap || {}),
        };

        Object.entries(incomingMap || {}).forEach(([entryKey, entryValues]) => {
            const currentValues = Array.isArray(mergedMap?.[entryKey])
                ? mergedMap[entryKey]
                : (mergedMap?.[entryKey] ? [mergedMap[entryKey]] : []);
            const normalizedIncomingValues = Array.isArray(entryValues)
                ? entryValues
                : (entryValues ? [entryValues] : []);

            mergedMap[entryKey] = _.uniq([...currentValues, ...normalizedIncomingValues].filter(Boolean));
        });

        mergedMeta[mapKey] = mergedMap;
    });

    return mergedMeta;
};

const resolveSourceFieldMeta = ({ fieldDefinitions, path, current }) => {
    const fieldDefinition = resolveFieldDefinition(fieldDefinitions, path);
    if (!fieldDefinition) {
        return {
            fieldDefinition: null,
            fieldId: null,
        };
    }

    const arrayIndexing = extractArrayIndexFromPath(path);

    return {
        fieldDefinition,
        fieldId: generateFieldId(fieldDefinition, arrayIndexing, current),
    };
};

const resolveFieldAutoSave = (fieldDefinitions, path) => {
    if (!fieldDefinitions || typeof fieldDefinitions !== 'object' || !path) {
        return null;
    }

    for (const candidate of [...getFieldCandidates(path), ...getInheritedFieldCandidates(path)]) {
        const fieldConfig = fieldDefinitions[candidate];
        if (fieldConfig?.autoSave) {
            return fieldConfig.autoSave;
        }
    }

    return null;
};

const matchesRouteRule = (path, route = {}) => {
    if (!path || !route || typeof route !== 'object') {
        return false;
    }

    const candidates = getFieldCandidates(path);
    if (route.field && candidates.includes(route.field)) {
        return true;
    }

    if (route.startsWith && candidates.some((candidate) => candidate.startsWith(route.startsWith))) {
        return true;
    }

    return Boolean(route.default);
};

const resolveRoute = ({ path, fieldDefinitions, autoSaveConfig }) => {
    const fieldAutoSave = resolveFieldAutoSave(fieldDefinitions, path);
    if (fieldAutoSave) {
        return fieldAutoSave;
    }

    const matchedRoute = (autoSaveConfig?.fieldRoutes || []).find((route) => matchesRouteRule(path, route));
    if (matchedRoute) {
        return matchedRoute;
    }

    return {
        // Default route is always the primary document unless JSON says otherwise.
        targetRecord: autoSaveConfig?.primaryRecord || 'primary',
        savePath: path,
    };
};

const resolveSavePath = ({ route, path }) => {
    if (route?.savePath) {
        return route.savePath;
    }

    const resolver = route?.recordResolver || {};

    if (resolver?.type === 'arrayItemByPath' && resolver?.arrayField) {
        const prefixPattern = new RegExp(`^${escapeRegExp(resolver.arrayField)}(?:\\[(\\d+)\\]|\\.(\\d+))(?:\\.|$)`);
        return path.replace(prefixPattern, '');
    }

    return path;
};

const resolveRuntimeRecordBinding = ({
    targetRecord,
    recordConfig,
    route,
    path,
    current,
    snapshot,
    runtimeRecords,
}) => {
    const primaryRecordAlias = route?.primaryRecordAlias || 'primary';
    if (targetRecord === primaryRecordAlias) {
        const preparedRecord = snapshot?._id === current?._id && snapshot?.version !== undefined
            ? { ...current, version: snapshot.version }
            : current;

        return {
            targetRecord,
            recordConfig,
            currentRecord: preparedRecord,
            snapshotRecord: snapshot,
            savePath: resolveSavePath({ route, path }),
            fieldDefinition: resolveFieldDefinition(route?.fieldDefinitions, path),
        };
    }

    const relatedRecord = runtimeRecords?.[targetRecord]
        || runtimeRecords?.[recordConfig?.source]
        || null;

    if (!relatedRecord?._id) {
        return null;
    }

    const preparedRecord = relatedRecord?.version !== undefined
        ? { ...relatedRecord, version: relatedRecord.version }
        : relatedRecord;

    return {
        targetRecord,
        recordConfig,
        currentRecord: preparedRecord,
        snapshotRecord: relatedRecord,
        savePath: resolveSavePath({ route, path }),
        fieldDefinition: resolveFieldDefinition(route?.fieldDefinitions, path),
    };
};

const resolveArrayItemBinding = ({
    targetRecord,
    recordConfig,
    route,
    path,
    current,
    snapshot,
}) => {
    const resolver = route?.recordResolver || {};
    const arrayField = resolver?.arrayField;

    if (!arrayField) {
        return null;
    }

    const match = path.match(new RegExp(`^${escapeRegExp(arrayField)}(?:\\[(\\d+)\\]|\\.(\\d+))(?:\\.|$)`));
    if (!match) {
        return null;
    }

    const arrayIndex = Number(match[1] ?? match[2]);
    if (!Number.isInteger(arrayIndex)) {
        return null;
    }

    const currentRecord = _.get(current, toLodashPath(`${arrayField}.${arrayIndex}`));
    const snapshotRecord = _.get(snapshot, toLodashPath(`${arrayField}.${arrayIndex}`));
    const resolvedCurrentRecord = currentRecord?._id ? currentRecord : snapshotRecord;
    const resolvedSnapshotRecord = snapshotRecord?._id ? snapshotRecord : currentRecord;

    if (!resolvedCurrentRecord?._id || !resolvedSnapshotRecord?._id) {
        return null;
    }

    const preparedRecord = {
        ...resolvedCurrentRecord,
        version: resolvedSnapshotRecord?.version ?? resolvedCurrentRecord?.version,
    };

    return {
        targetRecord,
        recordConfig,
        currentRecord: preparedRecord,
        snapshotRecord: resolvedSnapshotRecord,
        savePath: resolveSavePath({ route, path }),
        fieldDefinition: resolveFieldDefinition(route?.fieldDefinitions, path),
        meta: {
            arrayField,
            recordId: resolvedCurrentRecord?._id || resolvedSnapshotRecord?._id || null,
            matchLocalId: resolvedCurrentRecord?._localId || resolvedSnapshotRecord?._localId || null,
            structuralMode: STRUCTURAL_MODE.STANDALONE_RECORD_ARRAY,
        },
    };
};

const resolveRecordBinding = ({
    route,
    path,
    current,
    snapshot,
    autoSaveConfig,
    fieldDefinitions,
    runtimeRecords,
}) => {
    const primaryRecordAlias = autoSaveConfig?.primaryRecord || 'primary';
    const targetRecord = route?.targetRecord || primaryRecordAlias;
    const recordConfig = autoSaveConfig?.records?.[targetRecord];

    if (!recordConfig?.saveEndpoint || !recordConfig?.saveMode) {
        return null;
    }

    const contextualRoute = {
        ...route,
        fieldDefinitions,
        primaryRecordAlias,
    };

    const resolverType = contextualRoute?.recordResolver?.type
        || recordConfig?.resolver?.type
        || (targetRecord === primaryRecordAlias ? 'primary' : 'runtimeRecord');

    if (resolverType === 'arrayItemByPath') {
        return resolveArrayItemBinding({
            targetRecord,
            recordConfig,
            route: {
                ...recordConfig?.resolver,
                ...contextualRoute,
                recordResolver: {
                    ...(recordConfig?.resolver || {}),
                    ...(contextualRoute?.recordResolver || {}),
                },
            },
            path,
            current,
            snapshot,
        });
    }

    return resolveRuntimeRecordBinding({
        targetRecord,
        recordConfig,
        route: contextualRoute,
        path,
        current,
        snapshot,
        runtimeRecords,
    });
};

const resolveStructuralFieldConfigs = (fieldDefinitions = {}) => {
    const configs = new Map();

    Object.values(fieldDefinitions || {}).forEach((fieldDefinition) => {
        if (!fieldDefinition?.field || !fieldDefinition?.autoSave?.structural) {
            return;
        }

        const arrayField = normalizeArrayFieldPath(fieldDefinition.field);
        if (!arrayField || configs.has(arrayField)) {
            return;
        }

        configs.set(arrayField, {
            arrayField,
            fieldDefinition,
            autoSave: fieldDefinition.autoSave,
            structural: fieldDefinition.autoSave.structural,
        });
    });

    return [...configs.values()];
};

const readIdentityField = (item, fieldPath) => {
    if (!item || !fieldPath) {
        return null;
    }

    const value = _.get(item, toLodashPath(fieldPath));
    if (value === undefined || value === null || value === '') {
        return null;
    }

    return String(value);
};

const getIdentityFields = (structuralConfig = {}) => {
    if (Array.isArray(structuralConfig?.identityFields) && structuralConfig.identityFields.length > 0) {
        return structuralConfig.identityFields;
    }

    if (structuralConfig?.mode === STRUCTURAL_MODE.WHOLE_ARRAY_UPDATE_FIELD) {
        return ['aEId', '_id', '_localId', 'name', 'type'];
    }

    return ['_id', '_localId', 'aEId', 'cyclosUserId', 'cyclosUserName', 'email'];
};

const buildStructuralIdentity = (item, index, structuralConfig = {}) => {
    const identityFields = getIdentityFields(structuralConfig);
    for (const fieldPath of identityFields) {
        const value = readIdentityField(item, fieldPath);
        if (value) {
            return `${fieldPath}:${value}`;
        }
    }

    return `index:${index}`;
};

const diffArrayByIdentity = ({ previousArray = [], currentArray = [], structuralConfig = {} }) => {
    const previousBuckets = new Map();

    previousArray.forEach((item, index) => {
        const key = buildStructuralIdentity(item, index, structuralConfig);
        if (!previousBuckets.has(key)) {
            previousBuckets.set(key, []);
        }
        previousBuckets.get(key).push({ item, index, key });
    });

    const matched = [];
    const added = [];

    currentArray.forEach((item, index) => {
        const key = buildStructuralIdentity(item, index, structuralConfig);
        const bucket = previousBuckets.get(key);

        if (bucket?.length) {
            const previousEntry = bucket.shift();
            if (!bucket.length) {
                previousBuckets.delete(key);
            }
            matched.push({
                key,
                previousItem: previousEntry.item,
                previousIndex: previousEntry.index,
                currentItem: item,
                currentIndex: index,
            });
            return;
        }

        added.push({ key, item, index });
    });

    const removed = [];
    previousBuckets.forEach((bucket) => {
        bucket.forEach((entry) => removed.push(entry));
    });

    const previousSequence = previousArray.map((item, index) => buildStructuralIdentity(item, index, structuralConfig));
    const currentSequence = currentArray.map((item, index) => buildStructuralIdentity(item, index, structuralConfig));
    const hasReorder = !added.length
        && !removed.length
        && previousSequence.length === currentSequence.length
        && previousSequence.some((value, index) => value !== currentSequence[index]);

    return {
        added,
        removed,
        matched,
        hasStructuralChange: added.length > 0 || removed.length > 0,
        hasReorder,
    };
};

const deleteTransientCreateFields = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return payload;
    }

    const nextPayload = _.cloneDeep(payload);
    delete nextPayload._id;
    delete nextPayload.version;
    delete nextPayload._localId;
    delete nextPayload._isNew;
    return nextPayload;
};

const buildGroupedFieldOperation = ({ group }) => createFieldUpdateOperation({
    endpoint: group.endpoint,
    preparedData: group.preparedData,
    snapshotData: group.snapshotData,
    fieldDefinitions: group.localFieldDefinitions,
    fieldValueMap: group.fieldValueMap,
    recordKey: group.recordKey,
    label: group.label,
    meta: group.meta,
});

const pushFieldGroupValue = ({
    fieldGroups,
    groupKey,
    endpoint,
    recordKey,
    preparedData,
    snapshotData,
    savePath,
    value,
    fieldDefinition,
    sourcePath,
    sourceFieldId,
    label,
    meta = null,
}) => {
    const existingGroup = fieldGroups.get(groupKey) || {
        endpoint,
        recordKey,
        preparedData,
        snapshotData,
        label,
        meta,
        fieldValueMap: {},
        localFieldDefinitions: {},
    };

    existingGroup.fieldValueMap[savePath] = _.cloneDeep(value);
    if (fieldDefinition) {
        existingGroup.localFieldDefinitions[savePath] = fieldDefinition;
    }

    existingGroup.meta = mergeOperationMeta(existingGroup.meta, meta);
    existingGroup.meta = appendMetaMapValue(existingGroup.meta, 'sourceFieldPathMap', savePath, sourcePath);
    existingGroup.meta = appendMetaMapValue(existingGroup.meta, 'sourceFieldIdMap', savePath, sourceFieldId);

    fieldGroups.set(groupKey, existingGroup);
};

const buildStandaloneRecordArrayOperations = ({
    structuralEntry,
    snapshot,
    current,
    autoSaveConfig,
    fieldDefinitions,
}) => {
    const structuralConfig = structuralEntry.structural || {};
    const arrayField = structuralEntry.arrayField;
    const targetRecord = structuralEntry.autoSave?.targetRecord || autoSaveConfig?.primaryRecord || 'primary';
    const recordConfig = autoSaveConfig?.records?.[targetRecord];

    if (!recordConfig?.saveEndpoint) {
        return [];
    }

    const previousArray = Array.isArray(_.get(snapshot, toLodashPath(arrayField))) ? _.get(snapshot, toLodashPath(arrayField)) : [];
    const currentArray = Array.isArray(_.get(current, toLodashPath(arrayField))) ? _.get(current, toLodashPath(arrayField)) : [];
    const structuralDiff = diffArrayByIdentity({ previousArray, currentArray, structuralConfig });
    const excludePath = createAutoSaveExcludePath(autoSaveConfig);
    const fieldGroups = new Map();
    const operations = [];
    const createConfig = structuralConfig?.create || {};
    const deleteConfig = structuralConfig?.delete || {};

    structuralDiff.matched.forEach(({ previousItem, currentItem, currentIndex }) => {
        const changedPaths = buildFieldValueMap({
            snapshot: previousItem,
            current: currentItem,
            excludePath,
        });

        if (!Object.keys(changedPaths).length) {
            return;
        }

        if (!currentItem?._id && !previousItem?._id) {
            if (!createConfig?.endpoint) {
                return;
            }

            const payload = normalizeRecordPayload({
                path: `${arrayField}.${currentIndex}`,
                payload: deleteTransientCreateFields(currentItem),
                snapshotData: previousItem,
                fieldDefinitions,
            });
            const applicationIdField = createConfig?.applicationIdField || 'application_id';
            if (!payload?.[applicationIdField] && current?._id) {
                payload[applicationIdField] = current._id;
            }

            operations.push(createPostOperation({
                endpoint: createConfig.endpoint,
                payload,
                recordKey: targetRecord,
                label: `${arrayField}-create`,
                meta: {
                    arrayField,
                    matchLocalId: currentItem?._localId || null,
                    previousId: currentItem?._id || null,
                    insertIndex: currentIndex,
                    structuralMode: STRUCTURAL_MODE.STANDALONE_RECORD_ARRAY,
                },
            }));
            return;
        }

        if (!currentItem?._id || !previousItem?._id || currentItem._id !== previousItem._id) {
            return;
        }

        Object.entries(changedPaths).forEach(([relativePath, value]) => {
            const groupKey = `${targetRecord}::${currentItem._id}`;
            const sourcePath = `${arrayField}[${currentIndex}].${relativePath}`;
            const sourceFieldMeta = resolveSourceFieldMeta({
                fieldDefinitions,
                path: sourcePath,
                current,
            });
            pushFieldGroupValue({
                fieldGroups,
                groupKey,
                endpoint: recordConfig.saveEndpoint,
                recordKey: targetRecord,
                preparedData: {
                    ..._.cloneDeep(currentItem),
                    version: previousItem?.version ?? currentItem?.version,
                },
                snapshotData: _.cloneDeep(previousItem),
                savePath: relativePath,
                value,
                fieldDefinition: sourceFieldMeta.fieldDefinition
                    || resolveArrayItemFieldDefinition(fieldDefinitions, arrayField, relativePath, currentIndex),
                sourcePath,
                sourceFieldId: sourceFieldMeta.fieldId,
                label: `${arrayField}-item`,
                meta: {
                    arrayField,
                    recordId: currentItem?._id || previousItem?._id || null,
                    matchLocalId: currentItem?._localId || previousItem?._localId || null,
                    structuralMode: STRUCTURAL_MODE.STANDALONE_RECORD_ARRAY,
                },
            });
        });
    });

    operations.push(
        ...Array.from(fieldGroups.values()).map((group) => buildGroupedFieldOperation({ group }))
    );

    structuralDiff.added.forEach(({ item, index }) => {
        if (!createConfig?.endpoint) {
            return;
        }

        const payload = normalizeRecordPayload({
            path: `${arrayField}.${index}`,
            payload: deleteTransientCreateFields(item),
            snapshotData: null,
            fieldDefinitions,
        });
        const applicationIdField = createConfig?.applicationIdField || 'application_id';
        if (!payload?.[applicationIdField] && current?._id) {
            payload[applicationIdField] = current._id;
        }

        operations.push(createPostOperation({
            endpoint: createConfig.endpoint,
            payload,
            recordKey: targetRecord,
            label: `${arrayField}-create`,
            meta: {
                arrayField,
                matchLocalId: item?._localId || null,
                previousId: item?._id || null,
                insertIndex: index,
                structuralMode: STRUCTURAL_MODE.STANDALONE_RECORD_ARRAY,
            },
        }));
    });

    structuralDiff.removed.forEach(({ item }) => {
        if (!deleteConfig?.endpoint || !item?._id) {
            return;
        }

        operations.push(createDeleteOperation({
            endpoint: `${deleteConfig.endpoint}?_id=${encodeURIComponent(item._id)}`,
            recordKey: targetRecord,
            label: `${arrayField}-delete`,
            meta: {
                arrayField,
                deletedId: item._id,
                structuralMode: STRUCTURAL_MODE.STANDALONE_RECORD_ARRAY,
            },
        }));
    });

    return operations;
};

const buildWholeArrayStructuralOperations = ({
    structuralEntry,
    snapshot,
    current,
    autoSaveConfig,
    fieldDefinitions,
    runtimeRecords,
}) => {
    const structuralConfig = structuralEntry.structural || {};
    const arrayField = structuralEntry.arrayField;
    const previousArray = Array.isArray(_.get(snapshot, toLodashPath(arrayField))) ? _.get(snapshot, toLodashPath(arrayField)) : [];
    const currentArray = Array.isArray(_.get(current, toLodashPath(arrayField))) ? _.get(current, toLodashPath(arrayField)) : [];
    const comparablePreviousArray = normalizeRecordPayload({
        path: arrayField,
        payload: previousArray,
        snapshotData: previousArray,
        fieldDefinitions,
    }) || [];
    const comparableCurrentArray = normalizeRecordPayload({
        path: arrayField,
        payload: currentArray,
        snapshotData: previousArray,
        fieldDefinitions,
    }) || [];
    const structuralDiff = diffArrayByIdentity({
        previousArray: comparablePreviousArray,
        currentArray: comparableCurrentArray,
        structuralConfig,
    });
    const hasAnyArrayChange = !_.isEqual(comparablePreviousArray, comparableCurrentArray);

    if (!hasAnyArrayChange) {
        return {
            operations: [],
            shouldSuppressGenericDiff: false,
        };
    }

    if (structuralDiff.hasReorder && structuralConfig?.reorder === 'manual-save-only' && !structuralDiff.hasStructuralChange) {
        return {
            operations: [],
            shouldSuppressGenericDiff: true,
        };
    }

    const route = {
        ...(structuralEntry.autoSave || {}),
        savePath: structuralConfig?.savePath || arrayField,
    };

    const binding = resolveRecordBinding({
        route,
        path: arrayField,
        current,
        snapshot,
        autoSaveConfig,
        fieldDefinitions,
        runtimeRecords,
    });

    if (!binding?.currentRecord?._id) {
        return {
            operations: [],
            shouldSuppressGenericDiff: true,
        };
    }

    const operation = binding.recordConfig?.saveMode === 'full-update'
        ? createFullUpdateOperation({
            endpoint: binding.recordConfig.saveEndpoint,
            preparedData: _.set(_.cloneDeep(binding.currentRecord), toLodashPath(binding.savePath), _.cloneDeep(currentArray)),
            changeCount: 1,
            recordKey: binding.targetRecord,
            label: `${arrayField}-structural`,
            meta: mergeOperationMeta(binding.meta, {
                sourceFieldPathMap: {
                    [binding.savePath]: [arrayField],
                },
            }),
        })
        : createFieldUpdateOperation({
            endpoint: binding.recordConfig.saveEndpoint,
            preparedData: binding.currentRecord,
            snapshotData: binding.snapshotRecord,
            fieldDefinitions,
            fieldValueMap: {
                [binding.savePath]: _.cloneDeep(currentArray),
            },
            recordKey: binding.targetRecord,
            label: `${arrayField}-structural`,
            meta: mergeOperationMeta(binding.meta, {
                sourceFieldPathMap: {
                    [binding.savePath]: [arrayField],
                },
            }),
        });

    return {
        operations: [operation],
        shouldSuppressGenericDiff: true,
    };
};

const buildStructuralOperations = ({
    snapshot,
    current,
    autoSaveConfig,
    fieldDefinitions,
    runtimeRecords,
}) => {
    // Structural fields are planned first because they can suppress the generic diff
    // for the same array path and emit create/delete or whole-array operations instead.
    const comparisonSnapshot = _.cloneDeep(snapshot);
    const comparisonCurrent = _.cloneDeep(current);
    const operations = [];
    const excludePath = createAutoSaveExcludePath(autoSaveConfig);

    resolveStructuralFieldConfigs(fieldDefinitions).forEach((structuralEntry) => {
        if (structuralEntry?.structural?.mode === STRUCTURAL_MODE.STANDALONE_RECORD_ARRAY) {
            operations.push(
                ...buildStandaloneRecordArrayOperations({
                    structuralEntry,
                    snapshot,
                    current,
                    autoSaveConfig,
                    fieldDefinitions,
                })
            );

            _.set(comparisonSnapshot, toLodashPath(structuralEntry.arrayField), []);
            _.set(comparisonCurrent, toLodashPath(structuralEntry.arrayField), []);
            return;
        }

        if (structuralEntry?.structural?.mode === STRUCTURAL_MODE.WHOLE_ARRAY_UPDATE_FIELD) {
            const result = buildWholeArrayStructuralOperations({
                structuralEntry,
                snapshot,
                current,
                autoSaveConfig,
                fieldDefinitions,
                runtimeRecords,
            });

            operations.push(...(result?.operations || []));

            if (result?.shouldSuppressGenericDiff) {
                const currentArray = _.get(comparisonCurrent, toLodashPath(structuralEntry.arrayField));
                _.set(comparisonSnapshot, toLodashPath(structuralEntry.arrayField), _.cloneDeep(currentArray));
            }
        }
    });

    const omittedRoots = Array.isArray(autoSaveConfig?.rootOmissions) ? autoSaveConfig.rootOmissions : [];
    omittedRoots.forEach((omittedRoot) => {
        _.unset(comparisonSnapshot, toLodashPath(omittedRoot));
        _.unset(comparisonCurrent, toLodashPath(omittedRoot));
    });

    const omittedPaths = Array.isArray(autoSaveConfig?.pathOmissions) ? autoSaveConfig.pathOmissions : [];
    omittedPaths.forEach((omittedPath) => {
        _.unset(comparisonSnapshot, toLodashPath(omittedPath));
        _.unset(comparisonCurrent, toLodashPath(omittedPath));
    });

    return {
        operations,
        comparisonSnapshot,
        comparisonCurrent,
        excludePath,
    };
};

const buildGenericOperations = ({
    changedPaths,
    current,
    snapshot,
    autoSaveConfig,
    fieldDefinitions,
    runtimeRecords,
}) => {
    const fieldGroups = new Map();
    const fullGroups = new Map();

    Object.entries(changedPaths).forEach(([path, value]) => {
        const route = resolveRoute({
            path,
            fieldDefinitions,
            autoSaveConfig,
        });

        const binding = resolveRecordBinding({
            route,
            path,
            current,
            snapshot,
            autoSaveConfig,
            fieldDefinitions,
            runtimeRecords,
        });

        if (!binding?.currentRecord?._id) {
            return;
        }

        const groupKey = `${binding.targetRecord}::${binding.currentRecord._id}`;
        const sourceFieldMeta = resolveSourceFieldMeta({
            fieldDefinitions,
            path,
            current,
        });

        if (binding.recordConfig.saveMode === 'full-update') {
            const existingGroup = fullGroups.get(groupKey) || {
                endpoint: binding.recordConfig.saveEndpoint,
                recordKey: binding.targetRecord,
                payload: _.cloneDeep(binding.currentRecord),
                label: `${binding.targetRecord}-full`,
                changeCount: 0,
                meta: binding.meta || null,
            };

            _.set(existingGroup.payload, toLodashPath(binding.savePath), _.cloneDeep(value));
            existingGroup.changeCount += 1;
            existingGroup.meta = mergeOperationMeta(existingGroup.meta, binding.meta);
            existingGroup.meta = appendMetaMapValue(existingGroup.meta, 'sourceFieldPathMap', binding.savePath, path);
            existingGroup.meta = appendMetaMapValue(existingGroup.meta, 'sourceFieldIdMap', binding.savePath, sourceFieldMeta.fieldId);
            fullGroups.set(groupKey, existingGroup);
            return;
        }

        pushFieldGroupValue({
            fieldGroups,
            groupKey,
            endpoint: binding.recordConfig.saveEndpoint,
            recordKey: binding.targetRecord,
            preparedData: binding.currentRecord,
            snapshotData: binding.snapshotRecord,
            savePath: binding.savePath,
            value,
            fieldDefinition: sourceFieldMeta.fieldDefinition || binding.fieldDefinition,
            sourcePath: path,
            sourceFieldId: sourceFieldMeta.fieldId,
            label: `${binding.targetRecord}-field`,
            meta: binding.meta,
        });
    });

    return [
        ...Array.from(fieldGroups.values()).map((group) => buildGroupedFieldOperation({ group })),
        ...Array.from(fullGroups.values()).map((group) => createFullUpdateOperation({
            endpoint: group.endpoint,
            preparedData: group.payload,
            changeCount: group.changeCount,
            recordKey: group.recordKey,
            label: group.label,
            meta: group.meta,
        })),
    ];
};

const TRANSIENT_FRONTEND_LEAF_KEYS = new Set([
    '_localId',
    '_isNew',
    'hidePinCode',
    'ref_id',
    'name',
    'lat',
    'lng',
    'notAccurate',
]);

const TRANSIENT_ARRAY_IDENTITY_FIELDS = [
    '_id',
    'aEId',
    'cyclosUserId',
    'cyclosUserName',
    'email',
    'type',
    'name',
    'relations',
];

const readTransientArrayIdentity = (item, index) => {
    if (!_.isPlainObject(item)) {
        return `index:${index}`;
    }

    for (const fieldPath of TRANSIENT_ARRAY_IDENTITY_FIELDS) {
        const value = readIdentityField(item, fieldPath);
        if (value) {
            return `${fieldPath}:${value}`;
        }
    }

    return `index:${index}`;
};

const preserveTransientFrontendFields = (currentValue, nextValue) => {
    if (Array.isArray(currentValue) && Array.isArray(nextValue)) {
        const currentBuckets = new Map();

        currentValue.forEach((item, index) => {
            const identity = readTransientArrayIdentity(item, index);
            if (!currentBuckets.has(identity)) {
                currentBuckets.set(identity, []);
            }
            currentBuckets.get(identity).push(item);
        });

        return nextValue.map((item, index) => {
            const identity = readTransientArrayIdentity(item, index);
            const matchedCurrent = currentBuckets.get(identity)?.shift() ?? currentValue[index];
            return preserveTransientFrontendFields(matchedCurrent, item);
        });
    }

    if (_.isPlainObject(currentValue) && _.isPlainObject(nextValue)) {
        const mergedValue = _.cloneDeep(nextValue);

        Object.entries(currentValue).forEach(([key, currentChild]) => {
            if (mergedValue[key] === undefined && TRANSIENT_FRONTEND_LEAF_KEYS.has(key)) {
                mergedValue[key] = _.cloneDeep(currentChild);
                return;
            }

            if (mergedValue[key] !== undefined && (Array.isArray(mergedValue[key]) || _.isPlainObject(mergedValue[key]))) {
                mergedValue[key] = preserveTransientFrontendFields(currentChild, mergedValue[key]);
            }
        });

        return mergedValue;
    }

    return _.cloneDeep(nextValue);
};

const applyFieldValueMapToTarget = (target, fieldValueMap = {}, clearedFieldPaths = []) => {
    if (!target || typeof target !== 'object' || !fieldValueMap || typeof fieldValueMap !== 'object') {
        return target;
    }

    const nextTarget = _.cloneDeep(target);
    const clearedPathSet = new Set(Array.isArray(clearedFieldPaths) ? clearedFieldPaths : []);
    Object.entries(fieldValueMap).forEach(([path, value]) => {
        if (!path) {
            return;
        }

        if (value === undefined || clearedPathSet.has(path)) {
            _.unset(nextTarget, toLodashPath(path));
            return;
        }

        const currentValue = _.get(nextTarget, toLodashPath(path));
        _.set(nextTarget, toLodashPath(path), preserveTransientFrontendFields(currentValue, value));
    });

    return nextTarget;
};

const buildInsertedArrayItem = ({ operation, responseData, responseId, savedVersion }) => {
    const basePayload = operation?.type === 'create' && _.isPlainObject(operation?.payload)
        ? _.cloneDeep(operation.payload)
        : {};

    let nextItem = _.isPlainObject(basePayload) ? basePayload : {};

    if (operation?.payload?.fieldValueMap && typeof operation.payload.fieldValueMap === 'object') {
        nextItem = applyFieldValueMapToTarget(nextItem, operation.payload.fieldValueMap, operation?.meta?.clearedFieldPaths);
    }

    if (responseData && typeof responseData === 'object') {
        nextItem = {
            ...nextItem,
            ..._.cloneDeep(responseData),
        };
    }

    if (responseId) {
        nextItem._id = responseId;
    }

    if (savedVersion !== undefined) {
        nextItem.version = savedVersion;
    }

    if (operation?.type === 'create') {
        if (operation?.meta?.matchLocalId && nextItem?._localId === undefined) {
            nextItem._localId = operation.meta.matchLocalId;
        }
        delete nextItem._isNew;
    }

    return nextItem;
};

export const createJsonConfiguredAutoSaveAdapter = ({
    autoSaveConfig,
    fieldDefinitions = {},
    runtimeRecords = {},
    prepareData,
}) => {
    const resolvedConfig = autoSaveConfig || {};
    const primaryRecordAlias = resolvedConfig?.primaryRecord || 'primary';

    return {
        primaryRecordKey: primaryRecordAlias,
        prepareData(data) {
            return typeof prepareData === 'function' ? prepareData(data) : _.cloneDeep(data);
        },
        getTrackedRecords(data) {
            if (!data || typeof data !== 'object') {
                return [];
            }

            const trackedRecords = [];

            if (data?._id) {
                trackedRecords.push({
                    recordKey: primaryRecordAlias,
                    data,
                });
            }

            Object.entries(resolvedConfig?.records || {}).forEach(([recordKey, recordConfig]) => {
                if (recordKey === primaryRecordAlias) {
                    return;
                }

                const resolver = recordConfig?.resolver || {};
                if (resolver?.type !== 'arrayItemByPath' || !resolver?.arrayField) {
                    return;
                }

                const currentArray = _.get(data, toLodashPath(resolver.arrayField));
                if (!Array.isArray(currentArray)) {
                    return;
                }

                currentArray.forEach((item) => {
                    if (item && typeof item === 'object' && item._id) {
                        trackedRecords.push({
                            recordKey,
                            data: item,
                        });
                    }
                });
            });

            return _.uniqBy(trackedRecords, (entry) => `${entry?.recordKey || primaryRecordAlias}::${entry?.data?._id || ''}`);
        },
        applyOperationResult(data, operation, responseData) {
            if (!data || typeof data !== 'object') {
                return data;
            }

            const arrayField = operation?.meta?.arrayField;
            const responseId = responseData?._id || operation?.meta?.recordId || operation?.meta?.deletedId || operation?.payload?._id || null;
            const savedVersion = responseData?.version;

            if (!arrayField) {
                if (operation?.type === 'full' && operation?.payload && typeof operation.payload === 'object') {
                    const nextData = {
                        ..._.cloneDeep(operation.payload),
                        ...(responseData && typeof responseData === 'object' ? _.cloneDeep(responseData) : {}),
                    };

                    if (responseId) {
                        nextData._id = responseId;
                    }

                    if (savedVersion !== undefined) {
                        nextData.version = savedVersion;
                    }

                    return nextData;
                }

                let nextData = data;
                if (operation?.payload?.fieldValueMap && typeof operation.payload.fieldValueMap === 'object') {
                    nextData = applyFieldValueMapToTarget(nextData, operation.payload.fieldValueMap, operation?.meta?.clearedFieldPaths);
                }

                if (
                    responseId
                    && nextData?._id === responseId
                    && savedVersion !== undefined
                    && nextData?.version !== savedVersion
                ) {
                    nextData = {
                        ...nextData,
                        version: savedVersion,
                    };
                }

                return nextData;
            }

            const currentArray = _.get(data, toLodashPath(arrayField));
            if (!Array.isArray(currentArray)) {
                return data;
            }

            let hasChanged = false;
            const nextArray = currentArray.reduce((accumulator, item) => {
                const matchesLocalId = operation?.meta?.matchLocalId && item?._localId === operation.meta.matchLocalId;
                const matchesRecordId = responseId && item?._id === responseId;
                const matchesMetaRecordId = operation?.meta?.recordId && item?._id === operation.meta.recordId;
                const matchesPreviousId = operation?.meta?.previousId && item?._id === operation.meta.previousId;

                if (operation?.type === 'delete' && (matchesRecordId || matchesMetaRecordId || matchesLocalId || matchesPreviousId)) {
                    hasChanged = true;
                    return accumulator;
                }

                if (!matchesLocalId && !matchesRecordId && !matchesMetaRecordId && !matchesPreviousId) {
                    accumulator.push(item);
                    return accumulator;
                }

                hasChanged = true;
                // Backend create/update responses become the new source of truth for child rows,
                // including real _id/version replacing local draft identity markers.
                let nextItem = _.cloneDeep(item || {});

                if (operation?.payload?.fieldValueMap && typeof operation.payload.fieldValueMap === 'object') {
                    nextItem = applyFieldValueMapToTarget(nextItem, operation.payload.fieldValueMap, operation?.meta?.clearedFieldPaths);
                }

                if (responseData && typeof responseData === 'object') {
                    nextItem = {
                        ...nextItem,
                        ..._.cloneDeep(responseData),
                    };
                }

                if (responseId) {
                    nextItem._id = responseId;
                }

                if (savedVersion !== undefined) {
                    nextItem.version = savedVersion;
                }

                if (operation?.type === 'create') {
                    delete nextItem._isNew;
                }

                accumulator.push(nextItem);
                return accumulator;
            }, []);

            if (!hasChanged && operation?.type === 'create') {
                const insertedItem = buildInsertedArrayItem({
                    operation,
                    responseData,
                    responseId,
                    savedVersion,
                });
                const insertIndex = Number.isInteger(operation?.meta?.insertIndex)
                    ? operation.meta.insertIndex
                    : nextArray.length;
                nextArray.splice(Math.max(0, Math.min(insertIndex, nextArray.length)), 0, insertedItem);
                hasChanged = true;
            }

            if (!hasChanged) {
                return data;
            }

            const nextData = _.cloneDeep(data);
            _.set(nextData, toLodashPath(arrayField), nextArray);
            return nextData;
        },
        buildOperations({ snapshot, current }) {
            const structuralPlan = buildStructuralOperations({
                snapshot,
                current,
                autoSaveConfig: resolvedConfig,
                fieldDefinitions,
                runtimeRecords,
            });

            const changedPaths = buildFieldValueMap({
                snapshot: structuralPlan.comparisonSnapshot,
                current: structuralPlan.comparisonCurrent,
                excludePath: structuralPlan.excludePath,
            });

            const genericOperations = buildGenericOperations({
                changedPaths,
                current: structuralPlan.comparisonCurrent,
                snapshot: structuralPlan.comparisonSnapshot,
                autoSaveConfig: resolvedConfig,
                fieldDefinitions,
                runtimeRecords,
            });

            return createPlan({
                operations: [
                    ...structuralPlan.operations,
                    ...genericOperations,
                ],
            });
        },
    };
};
